import { useState } from "react";
import { MessageCircle, FileText, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { buildWhatsAppUrl } from "@/lib/whatsapp";
import FareQuoteDialog from "./FareQuoteDialog";

export interface Fare {
  id: string;
  zone: string;
  from_location: string;
  to_location: string;
  price_1_3: number | null;
  price_4_7: number | null;
  price_8_17: number | null;
  currency: string;
  is_new: boolean;
}

export const SEAT_TIERS = [
  { key: "price_1_3" as const, label: "1-3 places" },
  { key: "price_4_7" as const, label: "4-7 places" },
  { key: "price_8_17" as const, label: "8-17 places" },
];

export const fareWaMessage = (f: Fare, tierLabel?: string, price?: number | null) => {
  const base = `Bonjour Maroctransfers, je souhaite réserver un transfert ${f.from_location} → ${f.to_location}`;
  if (tierLabel && price) return `${base} (${tierLabel}, ~${price} ${f.currency}).`;
  return `${base}.`;
};

const normalize = (s: string) =>
  s
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[-/]/g, " ");

const rowMatches = (f: Fare, fromQuery?: string, toQuery?: string) => {
  const from = normalize(f.from_location);
  const to = normalize(f.to_location);
  const dep = normalize(fromQuery || "").trim();
  const arr = normalize(toQuery || "").trim();
  const depMatch = !dep || from.includes(dep);
  const arrMatch = !arr || to.includes(arr);
  return { depMatch, arrMatch, matches: depMatch && arrMatch };
};

const PriceCell = ({ f, tier }: { f: Fare; tier: (typeof SEAT_TIERS)[number] }) => {
  const price = f[tier.key];
  if (!price) {
    return <span className="text-muted-foreground font-bold">—</span>;
  }
  return (
    <a
      href={buildWhatsAppUrl(fareWaMessage(f, tier.label, price))}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-block rounded-md px-2 py-1 font-bold text-foreground hover:bg-primary/10 hover:text-primary transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      aria-label={`Réserver ${f.from_location} vers ${f.to_location} — ${tier.label} — ${price} ${f.currency}`}
    >
      {price} {f.currency}
    </a>
  );
};

const NewBadge = () => (
  <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-accent px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-accent-foreground align-middle">
    <Sparkles className="h-3 w-3" /> Nouveau
  </span>
);

const QuoteButton = ({
  f,
  full = false,
  onQuote,
}: {
  f: Fare;
  full?: boolean;
  onQuote: (f: Fare) => void;
}) => (
  <Button
    type="button"
    size="sm"
    variant="outline"
    onClick={() => onQuote(f)}
    className={`font-body font-semibold border-primary/40 text-primary hover:bg-primary/10 ${full ? "w-full" : ""}`}
    aria-label={`Demander un devis pour ${f.from_location} vers ${f.to_location}`}
  >
    <FileText className="mr-1 h-4 w-4" />
    Devis
  </Button>
);

const WaButton = ({ f, full = false }: { f: Fare; full?: boolean }) => (
  <Button
    asChild
    size="sm"
    className={`bg-[#25D366] hover:bg-[#1ebe5a] text-white font-body font-semibold ${full ? "w-full" : ""}`}
  >
    <a href={buildWhatsAppUrl(fareWaMessage(f))} target="_blank" rel="noopener noreferrer">
      <MessageCircle className="mr-1 h-4 w-4" />
      Réserver
    </a>
  </Button>
);

const FareTable = ({
  fares,
  highlightFrom,
  highlightTo,
}: {
  fares: Fare[];
  highlightFrom?: string;
  highlightTo?: string;
}) => {
  const [quoteFare, setQuoteFare] = useState<Fare | null>(null);

  return (
  <>
    {/* Desktop */}
    <div className="hidden md:block overflow-x-auto rounded-xl border border-border bg-card shadow-sm">
      <table className="w-full min-w-[780px] text-sm">
        <thead className="bg-primary text-primary-foreground">
          <tr>
            <th scope="col" className="px-4 py-3.5 text-left font-heading font-bold">Trajet</th>
            {SEAT_TIERS.map((tier) => (
              <th key={tier.key} scope="col" className="px-3 py-3.5 text-center font-heading font-bold">
                {tier.label}
              </th>
            ))}
            <th scope="col" className="px-3 py-3.5 text-right font-heading font-bold">Réservation</th>
          </tr>
        </thead>
        <tbody>
          {fares.map((f, idx) => {
            const { matches } = rowMatches(f, highlightFrom, highlightTo);
            return (
              <tr
                key={f.id}
                className={`${idx % 2 === 0 ? "bg-card" : "bg-muted/40"} ${
                  matches ? "bg-primary/10 ring-1 ring-inset ring-primary/40" : ""
                }`}
              >
                <td className="px-4 py-3.5 font-body font-semibold text-foreground">
                  {f.from_location}
                  <span className="mx-2 text-muted-foreground">→</span>
                  {f.to_location}
                  {f.is_new && <NewBadge />}
                </td>
                {SEAT_TIERS.map((tier) => (
                  <td key={tier.key} className="px-3 py-3.5 text-center font-body">
                    <PriceCell f={f} tier={tier} />
                  </td>
                ))}
                <td className="px-3 py-3.5">
                  <div className="flex justify-end gap-2">
                    <WaButton f={f} />
                    <QuoteButton f={f} onQuote={setQuoteFare} />
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>

    {/* Mobile */}
    <div className="space-y-4 md:hidden">
      {fares.map((f) => {
        const { matches } = rowMatches(f, highlightFrom, highlightTo);
        return (
          <div
            key={f.id}
            className={`rounded-xl border p-4 shadow-sm ${
              matches
                ? "border-primary bg-primary/10"
                : "border-border bg-card"
            }`}
          >
            <div className="font-heading font-bold text-foreground">
              {f.from_location}
              <span className="mx-2 text-muted-foreground">→</span>
              {f.to_location}
              {f.is_new && <NewBadge />}
            </div>
            <div className="my-3 grid grid-cols-3 gap-2">
              {SEAT_TIERS.map((tier) => (
                <div key={tier.key} className="rounded-md bg-muted/40 px-2 py-2 text-center">
                  <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{tier.label}</div>
                  <div className="font-heading text-sm">
                    <PriceCell f={f} tier={tier} />
                  </div>
                </div>
              ))}
            </div>
            <div className="grid grid-cols-2 gap-2">
              <WaButton f={f} full />
              <QuoteButton f={f} full onQuote={setQuoteFare} />
            </div>
          </div>
        );
      })}
    </div>

    <FareQuoteDialog fare={quoteFare} onOpenChange={(open) => !open && setQuoteFare(null)} />
  </>
  );
};

export default FareTable;
